import { SubscriptionRow } from '@/components/SubscriptionCard';
import { AppTour } from '@/components/AppTour';
import { GOALS } from '@/lib/goals';
import { MealPrefsRow } from '@/components/MealPreferences';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { useTheme } from '@/lib/theme';
import { api } from '@/lib/api';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  LogOut, Moon, Sun, User, Gift, MessageSquare, ChevronRight,
  CreditCard, Lock, Globe, Ruler, Lightbulb, Apple, Dumbbell,
  Snowflake, Brain, Pill, Bell, HelpCircle, FileText, Shield, Copy
} from 'lucide-react';
import { format } from 'date-fns';
import { FAQ, TERMS, PRIVACY, NOTIF_OPTIONS } from '@/lib/legalContent';
import { pushSupported, enablePush, disablePush, isPushActive, sendTestPush } from '@/lib/push';
import { PrivacySecurity } from '@/components/PrivacySecurity';
import { getSoundPref, getVibrationPref, setSoundPref, setVibrationPref } from '@/lib/workoutPrefs';
import { Sparkles, Trophy, ArrowRight } from 'lucide-react';
import { hasTier, TIER_LABEL } from '@/lib/tiers';

function SettingsUpgradeCard({ tier }) {
  const nav = useNavigate();
  if (hasTier(tier, 'regen_method')) return null;
  const toPlus = (tier || 'tracker') === 'tracker';
  const color = toPlus ? '#00C8FF' : '#65A30D';
  const title = toPlus ? "See what YourReGen+ unlocks" : "See what the YourReGen Protocol unlocks";
  const sub = toPlus
    ? 'Personalized training & nutrition, exercise videos, YourReGen Score and premium analytics.'
    : 'Work directly with Coach JP — coaching, weekly check-ins and program updates.';
  const target = toPlus ? '/choose-journey' : '/enroll/regen-method';
  return (
    <button onClick={() => nav(target)} data-testid="settings-upgrade-card"
            className="w-full text-left rounded-2xl p-5 relative overflow-hidden active:scale-[0.99] transition"
            style={{ border: `1px solid ${color}55`, background: `${color}10` }}>
      <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full blur-2xl" style={{ background: `${color}22` }} />
      <div className="relative flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${color}22` }}>
          {toPlus ? <Sparkles className="w-5 h-5" style={{ color }} /> : <Trophy className="w-5 h-5" style={{ color }} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-semibold" style={{ color }}>You're on {TIER_LABEL[tier || 'tracker']}</div>
          <div className="font-display font-black text-base">{title}</div>
          <div className="text-xs text-foreground/70 leading-snug">{sub}</div>
        </div>
        <ArrowRight className="w-5 h-5" style={{ color }} />
      </div>
    </button>
  );
}

export default function Settings() {
  const { user, logout, refresh } = useAuth();
  const { theme, toggle } = useTheme();
  const nav = useNavigate();
  const [tips, setTips] = useState([]);
  const [payments, setPayments] = useState([]);
  const [unread, setUnread] = useState(0);
  const [refs, setRefs] = useState([]);

  useEffect(() => {
    api.get('/tips').then(r => setTips(r.data));
    api.get('/payments/me').then(r => setPayments(r.data));
    api.get('/messages/unread/count').then(r => setUnread(r.data.count));
    api.get('/referrals').then(r => setRefs(r.data));
  }, []);

  const referralCode = (user?.id || '').slice(0, 8).toUpperCase();
  const referralLink = `${window.location.origin}/?ref=${referralCode}`;
  const currentPlan = payments.find(p => p.status === 'succeeded');
  const tipsByCategory = (cat) => tips.filter(t => t.category === cat);

  const copy = (text, msg = 'Copied') => {
    navigator.clipboard.writeText(text);
    toast.success(msg);
  };

  return (
    <div className="space-y-5 pb-8 settings-columns" data-testid="client-settings">
      <div>
        <div className="label-mini text-[#00C8FF]">Account</div>
        <h1 className="font-display font-black text-3xl mt-1">Settings</h1>
        <AppTour label="Replay the app guide" className="mt-2" />
      </div>

      {/* Profile header */}
      <div className="regen-card-elevated p-5">
        <div className="flex items-center gap-3">
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#00C8FF] to-[var(--c-green)] flex items-center justify-center text-[#081726] font-bold text-lg">
            {(user?.name || 'J').split(' ').map(p => p[0]).slice(0,2).join('')}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold truncate">{user?.name}</div>
            <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
            <div className="text-xs mt-0.5"><span className="pill bg-[#00C8FF]/10 text-[#00C8FF]">{user?.subscription?.status === 'active' ? (user.subscription.plan === 'regen_plus' ? 'YourReGen+' : 'YourReGen Protocol') : (user?.subscription_tier === 'tracker' ? 'YourReGen Tracker' : (user?.program || 'No Program'))}</span></div>
          </div>
        </div>
      </div>

      {/* Coach */}
      <SectionGroup label="Coach">
        <Row icon={MessageSquare} color="#00C8FF" title="Coach Chat" sub="Direct line to Coach JP" badge={unread > 0 ? unread : null} onClick={() => nav('/app/coach')} testid="row-coach-chat" />
      </SectionGroup>

      {/* Account */}
      <SectionGroup label="Account">
        <ProfileRow user={user} refresh={refresh} />
        <MealPrefsRow />
        <SubscriptionRow />
        <PasswordRow />
        <Row icon={Globe} color="#7DD3FC" title="Language" sub="English" onClick={() => toast.info('More languages coming soon')} testid="row-lang" />
        <Row icon={Ruler} color='#65A30D' title="Units" sub="Metric (kg, cm)" onClick={() => toast.info('Unit switching coming soon')} testid="row-units" />
      </SectionGroup>

      {/* Membership */}
      <SectionGroup label="Membership">
        <div className="px-4 py-3.5 border-b border-[var(--c-hair)] last:border-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-[var(--c-green-soft)]"><CreditCard className="w-4 h-4 text-[var(--c-green-ink)]" /></div>
            <div className="flex-1">
              <div className="text-sm font-medium">{user?.program || 'No active plan'}</div>
              <div className="text-[11px] text-muted-foreground">
                {currentPlan ? `Started ${format(new Date(currentPlan.created_at), 'MMM d, yyyy')} · ${currentPlan.plan_type === 'full' ? 'Paid in full' : 'Monthly'}` : 'Choose a plan from /checkout'}
              </div>
            </div>
          </div>
        </div>
        <Row icon={FileText} color="#FFB800" title="Payment History" sub={`${payments.length} transactions`} onClick={() => {
          if (payments.length === 0) return toast.info('No payments yet');
          toast.success(`Total: ${payments[0]?.currency} ${payments.reduce((s,p)=>s+p.amount,0).toFixed(2)}`);
        }} testid="row-payments" />
      </SectionGroup>

      {/* Referrals */}
      <SectionGroup label="Refer a Friend">
        <div className="p-4 border-b border-[var(--c-hair)]">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-[var(--c-green-soft)]"><Gift className="w-4 h-4 text-[var(--c-green-ink)]" /></div>
            <div>
              <div className="text-sm font-medium">Refer a Friend</div>
              <div className="text-[11px] text-muted-foreground">Share the YourReGen Protocol & you both win</div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 mb-3">
            <div className="rounded-xl bg-[var(--c-green-soft)] p-3" data-testid="referral-you-reward">
              <div className="text-[10px] uppercase tracking-wider text-[var(--c-green-ink)] font-bold">You receive</div>
              <div className="text-sm font-display font-bold mt-0.5">20% off your next renewal</div>
            </div>
            <div className="rounded-xl bg-[#00C8FF]/10 p-3" data-testid="referral-friend-reward">
              <div className="text-[10px] uppercase tracking-wider text-[#00C8FF] font-bold">Your friend gets</div>
              <div className="text-sm font-display font-bold mt-0.5">10% off their first membership</div>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <div className="flex-1 px-3 py-2 rounded-lg bg-[var(--c-film)] font-mono text-sm">{referralCode}</div>
              <Button size="sm" onClick={() => copy(referralCode, 'Code copied')} data-testid="copy-code" className="bg-[var(--c-green)] text-[#081726] font-semibold"><Copy className="w-3.5 h-3.5" /></Button>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex-1 px-3 py-2 rounded-lg bg-[var(--c-film)] text-xs truncate">{referralLink}</div>
              <Button size="sm" onClick={() => copy(referralLink, 'Link copied')} data-testid="copy-link" className="bg-[var(--c-film)] text-foreground"><Copy className="w-3.5 h-3.5" /></Button>
            </div>
            <div className="text-[11px] text-muted-foreground mt-2">{refs.length} {refs.length === 1 ? 'referral' : 'referrals'} sent</div>
          </div>
        </div>
      </SectionGroup>

      {/* Academy */}
      <SectionGroup label="Academy">
        <Row icon={Apple} color="#FF6FA8" title="YourReGen Academy" sub="Articles & Coach's Corner across all 5 pillars" onClick={() => nav('/app/academy')} testid="row-academy" />
      </SectionGroup>

      {/* Preferences */}
      <SectionGroup label="Preferences">
        <div className="px-4 py-3.5 border-b border-[var(--c-hair)] flex items-center gap-3" data-testid="appearance-row">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: theme === 'dark' ? '#00C8FF1a' : '#00B4D81a' }}>
            {theme === 'dark' ? <Moon className="w-4 h-4 text-[#00C8FF]" /> : <Sun className="w-4 h-4 text-[#00B4D8]" />}
          </div>
          <div className="flex-1">
            <div className="text-sm font-medium">Appearance</div>
            <div className="text-[11px] text-muted-foreground">{theme === 'dark' ? 'Dark Blue' : 'Light'}</div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-[11px] font-semibold ${theme === 'light' ? 'text-[#00B4D8]' : 'text-muted-foreground'}`}>Light</span>
            <Switch checked={theme === 'dark'} onCheckedChange={toggle} data-testid="theme-switch" />
            <span className={`text-[11px] font-semibold ${theme === 'dark' ? 'text-[#00C8FF]' : 'text-muted-foreground'}`}>Dark Blue</span>
          </div>
        </div>
        <NotificationSettings />
        <WorkoutPreferences />
      </SectionGroup>

      {/* Privacy & Security */}
      <PrivacySecurity />

      {/* Upgrade card */}
      <SettingsUpgradeCard tier={user?.subscription_tier} />

      {/* Support */}
      <SectionGroup label="Support">
        <FaqDialog />
        <Row icon={MessageSquare} color='#65A30D' title="Contact Us" sub="hello@yourregen.com" onClick={() => window.location.href = 'mailto:hello@yourregen.com'} testid="row-contact" />
      </SectionGroup>

      {/* Logout */}
      <Button variant="outline" onClick={logout} data-testid="client-logout" className="w-full h-12 border-[var(--c-hair)] hover:bg-red-500/10 hover:text-red-400 hover:border-red-500/30 rounded-xl">
        <LogOut className="w-4 h-4 mr-2" /> Logout
      </Button>
      <div className="text-center text-[11px] text-muted-foreground">YourReGen v1.0 · Built by Coach JP</div>
    </div>
  );
}

function SectionGroup({ label, children }) {
  return (
    <div>
      <div className="label-mini px-2 mb-1.5">{label}</div>
      <div className="regen-card-elevated overflow-hidden">{children}</div>
    </div>
  );
}

function Row({ icon: Icon, color, title, sub, onClick, badge, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className="w-full px-4 py-3.5 flex items-center gap-3 border-b border-[var(--c-hair)] last:border-0 hover:bg-[var(--c-film)] transition-all hover:translate-x-0.5 text-left">
      <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${color}1a` }}>
        <Icon className="w-4 h-4" style={{ color }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium">{title}</div>
        {sub && <div className="text-[11px] text-muted-foreground truncate">{sub}</div>}
      </div>
      {badge != null && <span className="pill bg-[#00C8FF] text-[#081726] text-[10px] font-bold min-w-[20px] text-center">{badge}</span>}
      <ChevronRight className="w-4 h-4 text-muted-foreground" />
    </button>
  );
}

function ProfileRow({ user, refresh }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const start = () => {
    const ob = user?.onboarding_data || {};
    setForm({
      name: user?.name || '', phone: user?.phone || '', country: user?.country || ob.country || '',
      city: ob.city || '', age: ob.age ?? '', gender: ob.gender || '',
      height_cm: ob.height_cm ?? '', current_weight_kg: ob.current_weight_kg ?? user?.current_weight_kg ?? '',
      target_weight_kg: ob.target_weight_kg ?? user?.goal_weight_kg ?? '', primary_goal: GOALS.includes(ob.primary_goal) ? ob.primary_goal : '',
    });
    setOpen(true);
  };
  const save = async () => {
    try {
      const payload = { ...form };
      ['age'].forEach(k => { if (payload[k] !== '') payload[k] = parseInt(payload[k]); else delete payload[k]; });
      ['height_cm', 'current_weight_kg', 'target_weight_kg'].forEach(k => { if (payload[k] !== '') payload[k] = parseFloat(payload[k]); else delete payload[k]; });
      if (!payload.primary_goal) delete payload.primary_goal;
      const r = await api.put('/auth/profile', payload); await refresh(); setOpen(false);
      toast.success(r.data?.programme_building ? 'Profile updated — building your programme now' : 'Profile updated');
    } catch { toast.error('Failed'); }
  };
  const fld = (k, label, type = 'text', ph) => (
    <div><Label>{label}</Label><Input type={type} value={form[k] ?? ''} placeholder={ph} onChange={e => setForm(prev => ({ ...prev, [k]: e.target.value }))} data-testid={`profile-${k}`} className="bg-transparent border-[var(--c-hair)]" /></div>
  );
  return (
    <>
      <Row icon={User} color="#00C8FF" title="Profile" sub="Edit your information" onClick={start} testid="row-profile" />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-regen-card border-[var(--c-hair)] max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit Profile</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {fld('name', 'Name')}
            {fld('phone', 'Phone')}
            <div className="grid grid-cols-2 gap-3">
              {fld('city', 'City')}
              {fld('country', 'Country')}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {fld('age', 'Age', 'number')}
              <div><Label>Sex</Label>
                <select value={form.gender || ''} onChange={e => setForm(prev => ({ ...prev, gender: e.target.value }))} data-testid="profile-gender" className="w-full h-10 rounded-md bg-transparent border border-[var(--c-hair)] px-3 text-sm">
                  <option value="">—</option><option>Male</option><option>Female</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {fld('height_cm', 'Height (cm)', 'number')}
              {fld('current_weight_kg', 'Weight (kg)', 'number')}
              {fld('target_weight_kg', 'Target (kg)', 'number')}
            </div>
            <div><Label>Goal</Label>
              <select value={form.primary_goal || ''} onChange={e => setForm(prev => ({ ...prev, primary_goal: e.target.value }))} data-testid="profile-primary_goal" className="w-full h-10 rounded-md bg-transparent border border-[var(--c-hair)] px-3 text-sm">
                <option value="" disabled>Choose your goal</option>{GOALS.map(g => <option key={g}>{g}</option>)}
              </select>
            </div>
            <Button onClick={save} data-testid="profile-save" className="w-full bg-[#00C8FF] text-[#081726] font-semibold">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function PasswordRow() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ current_password: '', new_password: '' });
  const save = async () => {
    try { await api.post('/auth/change-password', form); toast.success('Password changed'); setOpen(false); setForm({ current_password: '', new_password: '' }); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  return (
    <>
      <Row icon={Lock} color="#FF6FA8" title="Change Password" sub="Update your password" onClick={() => setOpen(true)} testid="row-password" />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-regen-card border-[var(--c-hair)]">
          <DialogHeader><DialogTitle>Change Password</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Current Password</Label><Input type="password" value={form.current_password} onChange={e => setForm({...form, current_password: e.target.value})} data-testid="pw-current" className="bg-transparent border-[var(--c-hair)]" /></div>
            <div><Label>New Password</Label><Input type="password" value={form.new_password} onChange={e => setForm({...form, new_password: e.target.value})} data-testid="pw-new" minLength={6} className="bg-transparent border-[var(--c-hair)]" /></div>
            <Button onClick={save} data-testid="pw-save" className="w-full bg-[#FF6FA8] text-[#081726] font-semibold">Update Password</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function InfoDialog({ icon, color, title, content, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Row icon={icon} color={color} title={title} sub="" onClick={() => setOpen(true)} testid={testid} />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-regen-card border-[var(--c-hair)] max-h-[85vh] overflow-y-auto" data-testid={`${testid}-dialog`}>
          <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
          <div className="text-sm leading-relaxed space-y-2 text-foreground/90">
            {content.split('\n').filter(l => l.trim() !== '').map((line, i) => {
              const t = line.trim();
              if (t.startsWith('## ')) return <h3 key={i} className="font-display font-bold text-base mt-3">{t.slice(3)}</h3>;
              if (t.startsWith('# ')) return <h2 key={i} className="font-display font-black text-lg">{t.slice(2)}</h2>;
              if (t.startsWith('- ')) return <li key={i} className="ml-4 list-disc">{renderBold(t.slice(2))}</li>;
              if (t.startsWith('_') && t.endsWith('_')) return <p key={i} className="text-xs text-muted-foreground">{t.replace(/_/g, '')}</p>;
              return <p key={i}>{renderBold(t)}</p>;
            })}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function renderBold(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => p.startsWith('**') && p.endsWith('**')
    ? <strong key={i} className="font-semibold text-foreground">{p.slice(2, -2)}</strong> : p);
}

function FaqDialog() {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);
  return (
    <>
      <Row icon={HelpCircle} color="#00C8FF" title="FAQ" sub={`${FAQ.length} common questions`} onClick={() => setOpen(true)} testid="row-faq" />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-regen-card border-[var(--c-hair)] max-h-[85vh] overflow-y-auto" data-testid="faq-dialog">
          <DialogHeader><DialogTitle>Frequently Asked Questions</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {FAQ.map((f, i) => (
              <div key={i} className="rounded-xl bg-[var(--c-film)] overflow-hidden">
                <button onClick={() => setActive(active === i ? null : i)} data-testid={`faq-q-${i}`} className="w-full px-4 py-3 flex items-center gap-2 text-left">
                  <span className="flex-1 text-sm font-medium">{f.q}</span>
                  <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${active === i ? 'rotate-90' : ''}`} />
                </button>
                {active === i && <div className="px-4 pb-3 text-sm text-foreground/80 leading-relaxed" data-testid={`faq-a-${i}`}>{f.a}</div>}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function WorkoutPreferences() {
  const [sound, setSound] = useState(getSoundPref());
  const [vib, setVib] = useState(getVibrationPref());
  const toggleSound = (v) => { setSound(v); setSoundPref(v); };
  const toggleVib = (v) => { setVib(v); setVibrationPref(v); };
  return (
    <>
      <div className="px-4 py-3.5 border-b border-[var(--c-hair)] flex items-center gap-3" data-testid="pref-workout-sound">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-[#00C8FF]/10"><Dumbbell className="w-4 h-4 text-[#00C8FF]" /></div>
        <div className="flex-1">
          <div className="text-sm font-medium">Recovery timer sound</div>
          <div className="text-[11px] text-muted-foreground">Beep + 3-2-1 countdown when recovery ends</div>
        </div>
        <Switch checked={sound} onCheckedChange={toggleSound} data-testid="workout-sound-switch" />
      </div>
      <div className="px-4 py-3.5 flex items-center gap-3" data-testid="pref-workout-vibration">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-[#00C8FF]/10"><Dumbbell className="w-4 h-4 text-[#00C8FF]" /></div>
        <div className="flex-1">
          <div className="text-sm font-medium">Recovery timer vibration</div>
          <div className="text-[11px] text-muted-foreground">Vibrate when recovery ends (where supported)</div>
        </div>
        <Switch checked={vib} onCheckedChange={toggleVib} data-testid="workout-vibration-switch" />
      </div>
    </>
  );
}


function NotificationSettings() {
  const [open, setOpen] = useState(false);
  const [prefs, setPrefs] = useState(null);
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [supported, setSupported] = useState(true);
  useEffect(() => {
    if (open && !prefs) api.get('/notifications/preferences').then(r => setPrefs(r.data));
    if (open) { setSupported(pushSupported()); isPushActive().then(setPushOn); }
  }, [open]);
  const toggle = async (key) => {
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    try { await api.put('/notifications/preferences', next); } catch { toast.error('Failed to save'); }
  };
  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (pushOn) { await disablePush(); setPushOn(false); toast.success('Push disabled'); }
      else { await enablePush(); setPushOn(true); toast.success('Push notifications enabled'); }
    } catch (e) { toast.error(e.message || 'Could not enable push'); }
    finally { setPushBusy(false); }
  };
  const test = async () => { try { const r = await sendTestPush(); if (r.data.sent > 0) toast.success('Test notification sent'); else toast.info('Enable push first'); } catch { toast.error('Failed'); } };
  return (
    <>
      <Row icon={Bell} color="#FFB800" title="Notifications" sub="Reminders, coach & achievements" onClick={() => setOpen(true)} testid="row-notif" />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-regen-card border-[var(--c-hair)] max-h-[85vh] overflow-y-auto" data-testid="notif-dialog">
          <DialogHeader><DialogTitle>Notification Settings</DialogTitle></DialogHeader>
          {!prefs ? <div className="text-sm text-muted-foreground py-6 text-center">Loading…</div> : (
            <div className="space-y-1">
              <div className="rounded-xl bg-[#FFB800]/10 p-3 mb-2">
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <div className="text-sm font-semibold flex items-center gap-1.5"><Bell className="w-4 h-4 text-[#FFB800]" /> Push Notifications</div>
                    <div className="text-[11px] text-muted-foreground">{supported ? 'Allow YourReGen to send push to this device' : 'Not supported on this browser'}</div>
                  </div>
                  <Switch checked={pushOn} disabled={!supported || pushBusy} onCheckedChange={togglePush} data-testid="push-master-toggle" />
                </div>
                {pushOn && <button onClick={test} data-testid="push-test-btn" className="mt-2 text-[11px] text-[#FFB800] font-semibold">Send test notification</button>}
              </div>
              {NOTIF_OPTIONS.map(o => (
                <div key={o.key} className="flex items-center gap-3 py-3 border-b border-[var(--c-hair)] last:border-0">
                  <div className="flex-1">
                    <div className="text-sm font-medium">{o.label}</div>
                    <div className="text-[11px] text-muted-foreground">{o.desc}</div>
                  </div>
                  <Switch checked={!!prefs[o.key]} onCheckedChange={() => toggle(o.key)} data-testid={`notif-${o.key}`} />
                </div>
              ))}
              <p className="text-[11px] text-muted-foreground pt-3">Install YourReGen to your home screen for the best push experience.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

